import { Component, OnDestroy, effect, input, signal } from '@angular/core';

/**
 * Efeito "digitando" das falas da IA: o texto aparece aos poucos, como nos
 * chats de IA atuais, em vez de pipocar inteiro.
 *
 * É só APRESENTAÇÃO — a resposta já chegou completa do backend; aqui ela é
 * revelada por caracteres com a velocidade ajustada para NUNCA passar de ~2,2s
 * no total (texto longo digita mais rápido, não mais demorado).
 *
 * O componente anima na criação: como a lista rastreia por `message.id`, cada
 * fala monta o componente uma única vez — mensagem antiga não re-anima.
 * `prefers-reduced-motion` mostra tudo de uma vez.
 */
@Component({
    selector: 'ia-chat-typewriter',
    template: `<p>{{ shown() }}@if (!done()) {<span class="tw-cursor" aria-hidden="true"></span>}</p>`,
    styles: [
        `
            /* O <p> vive DENTRO deste componente: os estilos de .bubble p do pai
               não atravessam o encapsulamento — replicamos o essencial. */
            p {
                margin: 0;
                white-space: pre-wrap;
            }

            .tw-cursor {
                display: inline-block;
                width: 0.5rem;
                height: 1em;
                margin-left: 0.125rem;
                border-radius: 0.125rem;
                background: var(--contentTertiary);
                vertical-align: text-bottom;
                animation: tw-blink 0.9s steps(1) infinite;
            }

            @keyframes tw-blink {
                50% {
                    opacity: 0;
                }
            }
        `,
    ],
})
export class IaChatTypewriterComponent implements OnDestroy {
    /** Texto completo da fala (já traduzido pelo chamador). */
    public text = input.required<string>();

    public shown = signal('');
    public done = signal(false);

    /** Duração-alvo máxima da animação inteira. */
    private static readonly MAX_DURATION_MS = 2200;
    private static readonly TICK_MS = 16;

    private _timer?: ReturnType<typeof setInterval>;

    constructor() {
        // `text` pode trocar depois de montado (ex.: troca de idioma resolve a
        // translationKey de novo): o effect reinicia/estende a revelação.
        effect(() => {
            const target = this.text() ?? '';
            this._animateTo(target);
        });
    }

    ngOnDestroy(): void {
        clearInterval(this._timer);
    }

    private _animateTo(target: string): void {
        clearInterval(this._timer);

        const reducedMotion =
            typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Texto encolheu (trocou) => recomeça; sem animação => mostra inteiro.
        const startAt = target.startsWith(this.shown()) ? this.shown().length : 0;
        if (reducedMotion || startAt >= target.length) {
            this.shown.set(target);
            this.done.set(true);
            return;
        }

        this.done.set(false);
        const remaining = target.length - startAt;
        const ticks = Math.max(1, IaChatTypewriterComponent.MAX_DURATION_MS / IaChatTypewriterComponent.TICK_MS);
        const charsPerTick = Math.max(1, Math.ceil(remaining / ticks));

        let cursor = startAt;
        this.shown.set(target.slice(0, cursor));
        this._timer = setInterval(() => {
            cursor = Math.min(target.length, cursor + charsPerTick);
            this.shown.set(target.slice(0, cursor));
            if (cursor >= target.length) {
                clearInterval(this._timer);
                this.done.set(true);
            }
        }, IaChatTypewriterComponent.TICK_MS);
    }
}
